import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import c from 'classnames';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { colorForTinyAccount } from 'helpers';
import { useTinyAccountNewUpdateState } from 'hooks/useNewUpdateIndicator';
import { getLatestKitForTinyAccountId } from 'store/kits';
import { getTinyAccountsOrderedByLatestKitResults } from '../../store/account';
import TinyAccount from 'types/TinyAccount';

import { NewFamilyMemberModal, NewUpdateDot } from 'components';
import { Icon, Typography } from '@repo/ui';

import styles from './TinyAccountSelector.module.css';

type FadingTextProps = {
  text: string;
  onClick?: () => void;
};
const FadingText: React.FC<FadingTextProps> = ({ text, onClick }) => {
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //if the text does overflow we add a class to fade the text instead of ellipsis
    if (textRef.current) {
      if (textRef.current.scrollWidth > textRef.current.clientWidth) {
        textRef.current.classList.add('fadeText');
      } else {
        textRef.current.classList.remove('fadeText');
      }
    }
  }, [text]);

  return (
    <div className={styles.avatarName} ref={textRef} onClick={onClick}>
      <Typography variant='caption'>{text}</Typography>
    </div>
  );
};
const TinyAccountAvatar = ({
  tinyAccount,
  isActive,
  backgroundColor,
  onSelect,
}: {
  tinyAccount: TinyAccount;
  isActive: boolean;
  backgroundColor: string;
  onSelect: (ta: TinyAccount) => void;
}) => {
  const latestKitId =
    useSelector(getLatestKitForTinyAccountId(tinyAccount.id))?.id ?? null;
  const { anyNewUpdate } = useTinyAccountNewUpdateState(
    tinyAccount.id,
    latestKitId,
  );

  return (
    <div
      key={tinyAccount.id}
      data-testid='familyAvatar'
      className={c(styles.avatarButton)}
    >
      <div
        className={c(
          styles.avatarBubble,
          styles.avatarBubbleWithDot,
          isActive ? styles.active : '',
        )}
        style={{ backgroundColor }}
        onClick={() => onSelect(tinyAccount)}
      >
        <Typography variant='heading-xl'>
          {tinyAccount.first_name.substr(0, 1)}
        </Typography>
        <NewUpdateDot
          show={anyNewUpdate}
          className={styles.avatarBubbleDot}
          ariaLabel={`New update for ${tinyAccount.first_name}`}
        />
      </div>
      <FadingText
        text={tinyAccount.first_name}
        onClick={() => onSelect(tinyAccount)}
      />
    </div>
  );
};

const AddTinyAccountButton = ({ onClick }: { onClick: () => void }) => (
  <div className={c(styles.avatarButton)} onClick={onClick}>
    <div
      data-testid='addNew'
      className={c(styles.avatarBubble, styles.bubbleAdd, 'btn')}
    >
      <Icon name='plus' size='m' />
    </div>
    <FadingText text='Add' />
  </div>
);
export const TinyAccountSelector = ({
  onSelection,
  currentTinyAccountId,
  withProfile = false,
  hideAdd = false,
}: {
  onSelection?: (tinyAccount?: Partial<TinyAccount>) => void;
  currentTinyAccountId?: string | null;
  withProfile?: boolean;
  hideAdd?: boolean;
}) => {
  const orderedTinyAccounts = useSelector(
    getTinyAccountsOrderedByLatestKitResults,
  );
  const { trackEvent } = useAnalytics();
  const [showModal, setShowModal] = useState(false);
  const carouselRef = useRef<Carousel | null>(null);
  useEffect(() => {
    if (orderedTinyAccounts.length > 0 && currentTinyAccountId) {
      const index = orderedTinyAccounts.findIndex(
        ta => ta.id === currentTinyAccountId,
      );
      if (index >= 0 && carouselRef.current) {
        carouselRef.current.goToSlide(index);
      }
    }
  }, [currentTinyAccountId, orderedTinyAccounts]);
  const handleSelection = (tinyAccount: TinyAccount) => {
    if (
      currentTinyAccountId &&
      tinyAccount.id &&
      tinyAccount.id !== currentTinyAccountId
    ) {
      trackEvent('Profile Switched', {
        from_profile_id: currentTinyAccountId,
        to_profile_id: tinyAccount.id,
      });
    }
    //call the function passed down on props with the selected family member (tinyAccount)
    if (onSelection && typeof onSelection === 'function') {
      onSelection(tinyAccount);
    }
  };

  return orderedTinyAccounts && orderedTinyAccounts.length === 0 ? null : (
    <>
      <NewFamilyMemberModal
        toggle={() => setShowModal(prev => !prev)}
        showModal={showModal}
        //after a new family member is created we select it (same as clicking on it afterwards)
        onFinishCallback={onSelection}
        withProfile={withProfile}
      />

      <Carousel
        ref={e => {
          carouselRef.current = e;
        }}
        responsive={{
          desktop: {
            breakpoint: { max: 10000, min: 950 },
            items: 2,
            slidesToSlide: 2,
          },
          mobile: {
            breakpoint: { max: 949, min: 0 },
            items: 3,
            slidesToSlide: 2,
          },
        }}
        className={styles.carousel}
        minimumTouchDrag={48}
        focusOnSelect={true}
        centerMode
      >
        {!hideAdd && orderedTinyAccounts?.length >= 4 && (
          <AddTinyAccountButton onClick={() => setShowModal(true)} />
        )}
        {orderedTinyAccounts?.map(ta => (
          <TinyAccountAvatar
            key={ta.id}
            tinyAccount={ta}
            isActive={currentTinyAccountId === ta.id}
            backgroundColor={colorForTinyAccount(orderedTinyAccounts, ta.id)}
            onSelect={handleSelection}
          />
        ))}

        {!hideAdd && orderedTinyAccounts.length < 4 && (
          <AddTinyAccountButton onClick={() => setShowModal(true)} />
        )}
      </Carousel>
    </>
  );
};
